import React, { useState, useEffect } from "react";
import { H2, DataField } from "./common-components";
import { ClinicalServiceTypes } from "../../patient/text/data";
import { formatDateToDD_MMM_YYYY } from "../../../helpers/functions";

const calculateAge = (dob) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return "";
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age.toString();
};

const formatServiceDate = (date) => date ? formatDateToDD_MMM_YYYY(date) : "";

const getNoteType = (note) => (note?.notePadType || note?.NotePadType || "").toLowerCase();

const isValidDate = (dateStr) => {
  if (!dateStr || dateStr.startsWith('0001-01-01')) return false;
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date.getTime()) && date.getUTCFullYear() > 1900;
};

const getNoteDate = (note) => {
  if (!note) return "";
  
  const systemPeriod = note.systemPeriod || note.SystemPeriod;
  if (systemPeriod) {
    const dateValue = typeof systemPeriod === 'string' 
      ? systemPeriod 
      : systemPeriod.lowerBound || systemPeriod.LowerBound;
    
    if (isValidDate(dateValue)) {
      return formatDateToDD_MMM_YYYY(dateValue);
    }
  }
  
  if (isValidDate(note.createdAt)) {
    return formatDateToDD_MMM_YYYY(note.createdAt);
  }
  
  return "";
};

const isPatientChartNote = (note, serviceId) => {
  const notePadClass = note?.notePadClass || note?.NotePadClass;
  const noteTitle = note?.title?.trim() || "";
  
  if (noteTitle.startsWith("Patient Chart")) {
    return true;
  }
  
  if (notePadClass === "patient") {
    return true;
  }
  
  if (!notePadClass && !serviceId) {
    return true;
  }
  
  return false;
};

const isPharmacistNote = (note) => {
  const type = getNoteType(note);
  return type === "pharmacistcomments" || type === "pharmacist";
};

const isPatientNote = (note) => {
  const type = getNoteType(note);
  return type === "patientcomments" || type === "patient";
};

const getCategoryName = (note, serviceId, clinicalServiceName) => {
  const notePadClass = note?.notePadClass || note?.NotePadClass;
  const noteTitle = note?.title?.trim();
  const isPatientChart = notePadClass === "patient" || (!notePadClass && !serviceId);
  
  if (isPatientChart) {
    if (noteTitle?.startsWith("Patient Chart")) {
      return noteTitle;
    }
    return "Patient Chart - Clinical Details";
  }
  
  if (notePadClass === "minorAilment") {
    return "Minor Ailments - Allergic Rhinitis";
  }
  
  if (!notePadClass && clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
    return "Minor Ailments - Allergic Rhinitis";
  }
  
  return "Annual Medication Review";
};

const getClinicalServiceDisplayName = (clinicalServiceName) => {
  if (!clinicalServiceName) return null;
  
  if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
    return "Annual Medication Review";
  }
  
  if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
    return "Allergic Rhinitis";
  }
  
  return clinicalServiceName;
};

const HeaderBar = ({ serviceId, clinicalServiceName, startDateTime }) => {
  const displayName = getClinicalServiceDisplayName(clinicalServiceName) || 
                      (serviceId ? "Clinical Service Name" : "Patient Chart");
  
  return (
    <div className="px-8 pt-6">
      <div className="bg-gray-20 border border-gray-200 rounded-lg py-2 px-4 flex justify-between items-center">
        <span className="text-gray-800 font-medium">
          {displayName}
        </span>
        <span className="text-gray-800 font-medium">
          Service Date {formatServiceDate(startDateTime)}
        </span>
      </div>
    </div>
  );
};

const PatientInfo = ({ patient }) => (
  <div className="px-8 pt-3 pb-2">
    <div className="bg-gray-20 border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-[310px]">
      <div className="flex flex-col gap-2">
        <DataField 
          label="Patient Name:" 
          value={[
            patient?.firstName,
            patient?.middleName,
            patient?.lastName
          ].filter(Boolean).join(" ") || ""}
          vertical={false}
        />
        <DataField 
          label="Date Of Birth:" 
          value={formatDateToDD_MMM_YYYY(patient?.dateOfBirth)}
          vertical={false}
        />
      </div>
      <div className="flex flex-col gap-2">
        <DataField 
          label="Health Card:" 
          value={patient?.healthCardNumber || ""}
          vertical={false}
        />
        <DataField 
          label="Age:" 
          value={calculateAge(patient?.dateOfBirth)}
          vertical={false}
        />
      </div>
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="bg-gray-20 rounded-lg p-4 border border-gray-200">
    <div className="text-gray-500 text-sm italic">{message}</div>
  </div>
);

const NoteCard = ({ note, serviceId, clinicalServiceName }) => {
  const categoryName = getCategoryName(note, serviceId, clinicalServiceName);
  const noteDate = getNoteDate(note);
  const commentText = note?.comments || note?.Comments || note?.comment || "";
  const noteTitle = note?.title?.trim();
  const displayTitle = noteTitle && noteTitle !== categoryName ? noteTitle : null;

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-20 px-4 py-2 flex justify-between items-center border-b border-gray-200">
        <div className="font-semibold text-gray-800">{categoryName}</div>
        {noteDate && (
          <div className="font-semibold text-gray-800">Date: {noteDate}</div>
        )}
      </div>
      
      <div className="bg-white px-4 py-2">
        {displayTitle && (
          <div className="mb-2">
            <div className="text-gray-600 text-sm font-medium mb-1">
              Title (Optional): {displayTitle}
            </div>
          </div>
        )}
        {commentText ? (
          <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
            {commentText}
          </div>
        ) : (
          <div className="text-gray-400 text-sm italic">
            No comment text available
          </div>
        )}
      </div>
    </div>
  );
};

const CommentsSection = ({ title, notes, serviceId, clinicalServiceName }) => (
  <div className="px-8 pt-2 pb-2">
    <H2 className="!text-teal-300 !text-lg !font-semibold mb-4">{title}</H2>
    <div className="space-y-4">
      {notes.length > 0 ? (
        notes.map((note, index) => (
          <NoteCard 
            key={note.id || index} 
            note={note} 
            serviceId={serviceId}
            clinicalServiceName={clinicalServiceName}
          />
        ))
      ) : (
        (title && (title.toLowerCase().startsWith('pharmacist') || title.toLowerCase().startsWith('patient')))
          ? null
          : <EmptyState message={`No ${title.toLowerCase()} available`} />
      )}
    </div>
  </div>
);

const NotesList = ({ 
  notepad = [], 
  clinicalServiceName, 
  patient, 
  startDateTime,
  serviceId = null,
  summaryId = null,
  commentType = null,
  showOnlyPatientChart = false
}) => {
  const [filteredNotes, setFilteredNotes] = useState([]);
  
  useEffect(() => {
    let notes = notepad;
    
    if (commentType === "pharmacist") {
      notes = notes.filter(isPharmacistNote);
    } else if (commentType === "patient") {
      notes = notes.filter(isPatientNote);
    }
    
    if (showOnlyPatientChart) {
      const patientChartNotes = notes.filter(note => isPatientChartNote(note, serviceId));
      notes = patientChartNotes.length > 0 ? patientChartNotes : notes;
    }

    // For clinical-service forms: exclude other-service notes but always include patient-chart notes.
    if (clinicalServiceName) {
      notes = notes.filter(note => {
        // Always include patient-chart notes
        if (isPatientChartNote(note, serviceId)) return true;

        const category = getCategoryName(note, serviceId, clinicalServiceName);

        if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
          // Include Minor Ailments notes
          return category && category.toLowerCase().includes('minor ailments');
        }

        if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
          // Include Annual Medication Review notes
          return category === 'Annual Medication Review' || (category && category.toLowerCase().includes('medication review'));
        }

        return true;
      });
    }
    
    setFilteredNotes(notes);
  }, [notepad, commentType, showOnlyPatientChart, serviceId]);

  const [pharmacistNotes, setPharmacistNotes] = useState([]);
  const [patientNotes, setPatientNotes] = useState([]);
  
  useEffect(() => {
    const pharmacist = filteredNotes.filter(isPharmacistNote);
    const patient = filteredNotes.filter(isPatientNote);
    setPharmacistNotes(pharmacist);
    setPatientNotes(patient);
  }, [filteredNotes]);

  const getSectionTitle = () => {
    if (commentType === "pharmacist") {
      return (filteredNotes && filteredNotes.length === 0)
        ? "Pharmacist Comments - No notes entered for this clinical service."
        : "Pharmacist Comments";
    }
    if (commentType === "patient") {
      return (filteredNotes && filteredNotes.length === 0)
        ? "Patient Comments - No notes entered for this clinical service."
        : "Patient Comments";
    }
    return "Comments";
  };

  if (!commentType) {
    return (
      <div className="min-h-[800px]">
        <HeaderBar 
          serviceId={serviceId} 
          clinicalServiceName={clinicalServiceName} 
          startDateTime={startDateTime} 
        />
        <PatientInfo patient={patient} />
        <CommentsSection 
          title="Pharmacist Comments" 
          notes={pharmacistNotes}
          serviceId={serviceId}
          clinicalServiceName={clinicalServiceName}
        />
        <CommentsSection 
          title="Patient Comments" 
          notes={patientNotes}
          serviceId={serviceId}
          clinicalServiceName={clinicalServiceName}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[800px]">
      <HeaderBar 
        serviceId={serviceId} 
        clinicalServiceName={clinicalServiceName} 
        startDateTime={startDateTime} 
      />
      <PatientInfo patient={patient} />
      <CommentsSection 
        title={getSectionTitle()} 
        notes={filteredNotes}
        serviceId={serviceId}
        clinicalServiceName={clinicalServiceName}
      />
    </div>
  );
};

export default NotesList;
